import React, { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Box, Flex, Input } from "@chakra-ui/react";
import { debounce } from "lodash";
import { colours } from "@/utils/themeUtils";
import { getUserDisplayTextForSelect } from "@/js/utils/userUtils";
import { searchUsers } from "@/js/api/usersApi";
import { UserInfo } from "@/js/api/authApi";

interface Props {
	selectStudent: (student: UserInfo) => void;
}

export function StudentSearchSelector(props: Props): JSX.Element {
	const [searchInput, setSearchInput] = useState<string>("");
	const [searchResults, setSearchResults] = useState<UserInfo[]>([]);
	const [isTyping, setIsTyping] = useState<boolean>(false);
	const [isSearching, setIsSearching] = useState<boolean>(false);
	const [showOptionsDropDown, setShowOptionsDropDown] = useState<boolean>(false);
	const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);

	const inputRef = useRef<HTMLInputElement>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);

	// Creates and cleans up an event listener for clicking outside the dropdown.
	// This allows us to keep the dropdown open when the user clicks on options in it.
	useEffect(() => {
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, []);

	// Calculate dropdown position when it should be shown
	useEffect(() => {
		if (showOptionsDropDown && inputRef.current) {
			const rect = inputRef.current.getBoundingClientRect();
			const gap = 4; // Small gap between input and dropdown
			setDropdownPosition({
				top: rect.bottom + gap,
				left: rect.left,
				width: rect.width,
			});
		} else {
			setDropdownPosition(null);
		}
	}, [showOptionsDropDown, searchResults]);

	// Handle window resize to recalculate dropdown position
	useEffect(() => {
		const handleResize = () => {
			if (showOptionsDropDown && inputRef.current) {
				const rect = inputRef.current.getBoundingClientRect();
				const gap = 4;
				setDropdownPosition({
					top: rect.bottom + gap,
					left: rect.left,
					width: rect.width,
				});
			}
		};

		const handleScroll = () => {
			if (showOptionsDropDown && inputRef.current) {
				const rect = inputRef.current.getBoundingClientRect();
				const gap = 4;
				setDropdownPosition({
					top: rect.bottom + gap,
					left: rect.left,
					width: rect.width,
				});
			}
		};

		window.addEventListener("resize", handleResize);
		window.addEventListener("scroll", handleScroll, true); // Use capture to catch all scroll events

		return () => {
			window.removeEventListener("resize", handleResize);
			window.removeEventListener("scroll", handleScroll, true);
		};
	}, [showOptionsDropDown]);

	const debouncedSearch = useRef(
		debounce((searchTerm: string) => {
			setIsTyping(false);
			setIsSearching(true);
			searchUsers(searchTerm)
				.then((results) => {
					setSearchResults(results);
				})
				.catch((error) => {
					console.error("Error searching for students:", error);
					setSearchResults([]);
				})
				.finally(() => {
					setIsSearching(false);
				});
		}, 500)
	).current;

	function handleClickOutside(event: MouseEvent) {
		if (
			inputRef.current &&
			!inputRef.current.contains(event.target as Node) &&
			dropdownRef.current &&
			!dropdownRef.current.contains(event.target as Node)
		) {
			setShowOptionsDropDown(false);
		}
	}

	function handleChangeStudentSearchTerm(event: ChangeEvent<HTMLInputElement>) {
		const searchTerm = event.target.value;

		setSearchInput(searchTerm);

		if (searchTerm.length < 3) {
			setIsSearching(false);
			setIsTyping(false);
			setSearchResults([]);
			debouncedSearch.cancel();
			return;
		}

		setIsTyping(true);
		debouncedSearch(searchTerm);
	}

	function handleInputKeyDown(event: KeyboardEvent<HTMLInputElement>) {
		setShowOptionsDropDown(true);

		// This makes escape key close the search results dropdown, but not the dialog/drawer when focused on the input field
		if (event.key === "Escape") {
			event.stopPropagation();
			(event.target as HTMLInputElement).blur();
			setShowOptionsDropDown(false);
		}

		// If there is exactly one result and enter is pressed, select that result
		if (event.key === "Enter" && searchResults.length === 1 && !isTyping && !isSearching) {
			event.preventDefault();
			event.stopPropagation();
			handleChooseStudent(searchResults[0]);
			setShowOptionsDropDown(false);
		}
	}

	function handleChooseStudent(student: UserInfo): void {
		if (student.stableIdV1 === "") return;

		setSearchInput("");
		setSearchResults([]);

		props.selectStudent(student);
	}

	const renderDropdown = () => {
		if (!showOptionsDropDown || !dropdownPosition) return null;

		const dropdownContent = (
			<Box
				ref={dropdownRef}
				background={"neutrals.0"}
				position={"fixed"}
				top={`${dropdownPosition.top}px`}
				left={`${dropdownPosition.left}px`}
				width={`${dropdownPosition.width}px`}
				boxShadow={"0px 3px 10px rgba(0, 0, 0, 0.3)"}
				borderRadius={"md"}
				overflow={"scroll"}
				zIndex={9999}
				maxHeight={"300px"}
			>
				{!isTyping && isSearching && (
					<Box color={"neutrals.300"} px={6} py={2} data-testid="searching-message">
						Searching...
					</Box>
				)}
				{searchInput.length < 3 && (
					<Box color={"neutrals.300"} px={6} py={2} data-testid="minchar-message">
						Enter at least 3 characters to search
					</Box>
				)}

				{!isTyping && !isSearching && searchInput.length > 2 && searchResults.length === 0 && (
					<Box color={"neutrals.300"} px={6} py={2} data-testid="no-results-message">
						No results found
					</Box>
				)}

				{!isSearching &&
					searchResults.map((student, index) => (
						<Flex
							as={"button"}
							display={"block"}
							width={"100%"}
							alignItems={"center"}
							textAlign={"left"}
							cursor={"pointer"}
							key={index}
							onClick={() => {
								handleChooseStudent(student);
								setShowOptionsDropDown(false);
							}}
							_hover={{
								background: colours.neutrals[50],
							}}
							transition={"background 0.2s"}
							px={6}
							py={3}
						>
							{getUserDisplayTextForSelect(student)}
						</Flex>
					))}
			</Box>
		);

		// Use portal to render the dropdown outside of any modals so that it doesn't get cut off if it exceeds the modal.
		return createPortal(dropdownContent, document.body);
	};

	return (
		<Box position={"relative"}>
			<Input
				mt={0}
				ref={inputRef}
				autoComplete="off"
				placeholder={"Enter student name to see matches"}
				value={searchInput}
				onChange={handleChangeStudentSearchTerm}
				onClick={() => setShowOptionsDropDown(true)}
				data-testid="student-search-input"
				onKeyDown={handleInputKeyDown}
			/>

			{renderDropdown()}
		</Box>
	);
}
